import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';

/**
 * Confirm supplier preset before Gemini OCR.
 * `detection` shape from detect-invoice-supplier:
 *   { suggestedConfigId, suggestedDisplayName, configs: [{_id, sourceName, supplierDisplayName}] }
 */
export default function SupplierConfirmDialog({
    open,
    detection,
    loading,
    onCancel,
    onConfirm,
}) {
    const configs = detection?.configs || [];
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        if (!open || !detection) {
            setSelected(null);
            return;
        }
        const suggested = configs.find((c) => c._id === detection.suggestedConfigId) || null;
        setSelected(suggested);
    }, [open, detection]);

    const options = configs.map((c) => ({
        ...c,
        label: c.supplierDisplayName || c.sourceName,
    }));

    return (
        <Dialog open={open} onClose={onCancel} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 600, fontSize: 16 }}>Which supplier is this?</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Pick the supplier so we know how to read this invoice. Processing starts after you confirm.
                </Typography>

                {loading ? (
                    <div className="d-flex justify-content-center py-3">
                        <CircularProgress size={28} />
                    </div>
                ) : (
                    <>
                        {detection?.suggestedDisplayName ? (
                            <Alert severity="info" sx={{ mb: 2, fontSize: 13 }}>
                                Best match: <strong>{detection.suggestedDisplayName}</strong> — change it below if that’s wrong.
                            </Alert>
                        ) : (
                            <Alert severity="warning" sx={{ mb: 2, fontSize: 13 }}>
                                Couldn’t auto-detect. Choose the supplier from the list.
                            </Alert>
                        )}

                        <Autocomplete
                            options={options}
                            getOptionLabel={(o) => o.label || o.sourceName || ''}
                            value={selected}
                            onChange={(_, v) => setSelected(v)}
                            isOptionEqualToValue={(a, b) => a?._id === b?._id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Supplier"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                />
                            )}
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onCancel} sx={{ textTransform: 'none', color: '#667085' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    disabled={!selected || loading}
                    onClick={() => onConfirm(selected)}
                    sx={{ textTransform: 'none', backgroundColor: '#102A43', '&:hover': { backgroundColor: '#1a3d5c' } }}
                >
                    Confirm &amp; read invoice
                </Button>
            </DialogActions>
        </Dialog>
    );
}
